import React from 'react';

export type LogoVariant = 'color' | 'black' | 'white' | 'transparent';
export type LogoSize = 'sm' | 'md' | 'lg' | 'xl';

interface LogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  className?: string;
  showText?: boolean;
  textClassName?: string;
}

const sizeClasses = {
  sm: 'h-6 w-6',
  md: 'h-8 w-8', 
  lg: 'h-12 w-12',
  xl: 'h-16 w-16'
};

const textSizeClasses = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl'
};

export const Logo: React.FC<LogoProps> = ({ 
  variant = 'color', 
  size = 'md', 
  className = '',
  showText = true,
  textClassName = ''
}) => {
  const getLogoSrc = () => {
    const basePath = '/logos/png';
    
    switch (variant) {
      case 'black':
        return `${basePath}/logo-black.png`;
      case 'white':
        return `${basePath}/logo-white.png`;
      case 'transparent':
        return `${basePath}/logo-no-background.png`;
      case 'color':
      default:
        return `${basePath}/logo-color.png`;
    }
  };

  const getTextColor = () => {
    switch (variant) {
      case 'white':
        return 'text-white';
      case 'black':
        return 'text-black dark:text-white';
      case 'transparent':
        return 'text-indigo-700 dark:text-indigo-300';
      case 'color':
      default:
        return 'text-indigo-700 dark:text-indigo-300';
    }
  };

  return (
    <div className={`flex items-center ${className}`}>
      <img 
        src={getLogoSrc()} 
        alt="IntFinder" 
        className={`${sizeClasses[size]} object-contain`}
      />
      {showText && (
        <span className={`ml-2 font-bold ${textSizeClasses[size]} ${getTextColor()} ${textClassName}`}>
          IntFinder
        </span>
      )}
    </div>
  );
};

// SVG Logo Component for better scalability
export const SVGLogo: React.FC<Omit<LogoProps, 'size'> & { width?: number; height?: number }> = ({ 
  variant = 'color', 
  width = 32, 
  height = 32,
  className = '',
  showText = true,
  textClassName = ''
}) => {
  const getLogoSrc = () => {
    const basePath = '/logos/svg';
    
    switch (variant) {
      case 'black':
        return `${basePath}/logo-black.svg`;
      case 'white':
        return `${basePath}/logo-white.svg`;
      case 'transparent':
        return `${basePath}/logo-no-background.svg`;
      case 'color':
      default:
        return `${basePath}/logo-color.svg`;
    }
  };

  const getTextColor = () => {
    switch (variant) {
      case 'white':
        return 'text-white';
      case 'black':
        return 'text-black dark:text-white';
      case 'transparent':
        return 'text-indigo-700 dark:text-indigo-300';
      case 'color':
      default:
        return 'text-indigo-700 dark:text-indigo-300';
    }
  };

  return (
    <div className={`flex items-center ${className}`}>
      <img 
        src={getLogoSrc()} 
        alt="IntFinder" 
        width={width}
        height={height}
        className="object-contain"
      />
      {showText && (
        <span className={`ml-2 font-bold ${getTextColor()} ${textClassName}`}>
          IntFinder
        </span>
      )}
    </div>
  );
};
